import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {GuestToken} from '../../../../security/services/SSO';
import {
  ApplicationStatusState,
  GetError,
} from '../../../../security/model/application-status';
import React, {useState} from 'react';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {patientService} from '../../../../patient/services/patient-service';
import {
  specialistClientService,
} from '../../../../implementation-specialist/services/implementation-client-service';
import {AuthorizationDocumentStatus} from '../payment-program-enrollment/payment-program-enrollment-page'
import {getDynamicContent} from '../no-sig-dynamic-content';
import {ApprovalView} from '../approval-view';
import {getMethodApprovedFromSearch} from '../get-method-approved';
import {commentsService} from '@finpay-development/shared-platform/lib/esm/admin/services/comments-service';

interface PaymentAgreementViewProps {
  patientEncounter: PatientEncounter;
  payment: Record<string, any>
  selectedAuthDocStatus: AuthorizationDocumentStatus | null;
  guestToken: GuestToken | null;
  setMessage: (msg: ApplicationStatusState) => void;
  authDocPdfBlob: Blob | null
}

export const PaymentApprovalView = (props: PaymentAgreementViewProps) => {

  const {
    patientEncounter,
    payment,
    guestToken,
    selectedAuthDocStatus,
    setMessage,
    authDocPdfBlob
  } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [isEnrollmentThankYou, setIsEnrollmentThankYou] = useState(false);
  const [clientDetails, setClientDetails] = useState({
    surveyUrl: '',
    clientName: '',
    email: '',
    phoneNumber: '',
  });

  const handleApproveBtnOnClick = async () => {

    HttpInterceptor(false, guestToken!, true);

    const resolvedPromises = await Promise.all([
      processAuthDocApproval({
        authorizationDocumentStatusId: selectedAuthDocStatus?.authorizationDocumentStatusId!,
        patientEncounterId: patientEncounter.patientEncounterId,
        patientId: patientEncounter.patientId,
        paymentAmt: payment.paymentAmt
      }),
      //getting survey url path
      specialistClientService.getClient(patientEncounter.clientId),
    ]);

    for (let i = 0; i < resolvedPromises.length; i++) {
      if (resolvedPromises?.[i]?.hasErrors) {
        setMessage(GetError(
            'A problem occurred while approving your payment. Please reach out to a FinPay administrator.'));
        return;
      }
    }

    const fetchedClientDetail = resolvedPromises[1]?.entity?.fullClient;

    if(
        fetchedClientDetail.clientCrm.length > 0 &&
        fetchedClientDetail.clientCrm?.[0]?.crmType?.crmTypeSlug &&
        patientEncounter.clientsPatientIOCId
    ){

      const integrationUpdateResponse = await patientService.integrationUpdate({
        patientEncounterId: patientEncounter.patientEncounterId,
        patientId: patientEncounter.patientId,
        crmTypeSlug: fetchedClientDetail.clientCrm?.[0].crmType.crmTypeSlug
      })

      if(integrationUpdateResponse.hasErrors){
        console.error("Error occurred during real time update")
      }

    }

    setIsEnrollmentThankYou(true);
    setClientDetails({
      ...clientDetails,
      surveyUrl: fetchedClientDetail?.surveyUrl,
      clientName: fetchedClientDetail?.clientName,
      email: fetchedClientDetail?.primaryContact?.email,
      phoneNumber: fetchedClientDetail?.primaryContact?.primPhoneNum,
    });

  };

  const dynamicContent = getDynamicContent({
    typeOfDoc: selectedAuthDocStatus?.authorizationDocuments?.typeOfDoc!,
    patientEncounter,
    modalState: {
      state: modalOpen,
      setState: setModalOpen
    },
    pdfBlob: authDocPdfBlob,
    payment
  })

  return (
      <ApprovalView
          pageContent={{
            title: `${patientEncounter?.client?.clientName} at ${patientEncounter?.facilityName} Payment Summary`,
            body: {
              intro: 'Thank you for your recent payment.',
              details: dynamicContent?.paymentDetails!,
              explanation:  payment.ledger.ledgerTypeId === 4 ? 'Pending payments will be credited to your balance once the payment is finalized.' : "",


              instructions: 'for applicable Terms & Conditions.',
              checkboxLabel: "I authorize this payment and acknowledge that this payment, and payments made to date, and any future payments are authorized and subject to the Terms & Conditions."
            },
            footerNotes: [
             'This amount does not reflect your total Patient Financial Responsibility as it may change depending on any insurance coverages & the services you receive.',
              '* Paid to date payments may not reflect payments towards other instances of care or pending payments that have not been finalized.'
            ]
          }}
          docModal={dynamicContent.docModal}
          displayThankYouView={isEnrollmentThankYou}
          clientDetails={clientDetails}
          modalOpenState={[modalOpen, setModalOpen]}
          setMessage={setMessage}
          handleApprovedBtnOnClick={handleApproveBtnOnClick}
          thankYouViewConfig={{
            thankYouText: 'Thank you for approving the payment!',
          }}
      />
  );

};

const processAuthDocApproval = async (params : {
  authorizationDocumentStatusId: number
  patientEncounterId: number
  patientId: number
  paymentAmt: number
}) => {

  const {authorizationDocumentStatusId, patientId, patientEncounterId, paymentAmt} = params;
  const methodApproved = getMethodApprovedFromSearch();

  const authDocResponse = await patientService.updateAuthorizationDocumentStatus(
      {
        dataToSave: {
          documentStatus: 'signed',
          dateApproved: new Date().toISOString(),
          ...methodApproved && {methodApproved},
        },
        authorizationDocumentStatusId: authorizationDocumentStatusId,
      });

  if (authDocResponse?.hasErrors) {
    return authDocResponse;
  }

  const docCreationResponse =  await patientService.createNoSigHelloSignDoc({
    authorizationDocumentStatusId,
  });

  if (docCreationResponse?.hasErrors) {

    if(docCreationResponse?.hasErrors) {
      await patientService.updateAuthorizationDocumentStatus(
          {
            dataToSave: {
              documentStatus: 'sent',   //reverting status back to sent to avoid conflicts
              dateApproved: null
            },
            authorizationDocumentStatusId: authorizationDocumentStatusId,
          });
    }

    return docCreationResponse;

  }else{

    await commentsService.createPatientNote({
      patientId,
      patientEncounterId,
      noteText: `One Time Payment Agreement for $${paymentAmt} has been approved.`,
      noteDt: new Date().toISOString()
    })

  }

};

